const WEEK_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const MONTH_LABELS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
let calendarId = 0;

export class Calendar {
  constructor(root) {
    this.root = root;
    if (!this.root) return;

    this.dataEl = this.root.querySelector('.l-calendar__data');
    this.inner = this.root.querySelector('.l-calendar__inner');
    if (!this.dataEl || !this.inner) return;

    this.instanceId = calendarId++;
    this.months = this.parseData().filter(
      (m) => m && Number.isInteger(m.year) && Number.isInteger(m.month) && m.month >= 1 && m.month <= 12,
    );
    if (this.months.length === 0) return;

    this.mobileMediaQuery = window.matchMedia('(max-width: 767px)');
    this.activeMonthIndex = 0;
    this.tabNav = null;
    this.tabButtons = [];
    this.monthElements = [];

    this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
    this.handleTabClick = this.handleTabClick.bind(this);
    this.handleTabKeydown = this.handleTabKeydown.bind(this);

    this.render();

    if (typeof this.mobileMediaQuery.addEventListener === 'function') {
      this.mobileMediaQuery.addEventListener('change', this.handleBreakpointChange);
    } else if (typeof this.mobileMediaQuery.addListener === 'function') {
      this.mobileMediaQuery.addListener(this.handleBreakpointChange);
    }
  }

  parseData() {
    try {
      const parsed = JSON.parse(this.dataEl.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  buildWeeks(year, month, closedDates) {
    const closed = Array.isArray(closedDates) ? new Set(closedDates.map(Number)) : new Set();
    const firstDay = (new Date(year, month - 1, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month, 0).getDate();
    const cells = Array(firstDay).fill(null);

    for (let day = 1; day <= daysInMonth; day++) {
      const status = closed.has(day) ? 'close' : 'open';
      cells.push({ day, status, muted: status === 'close' });
    }

    while (cells.length % 7 !== 0) cells.push(null);

    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
  }

  createHeading(monthData) {
    const heading = document.createElement('div');
    heading.className = 'l-calendar__heading';

    const num = document.createElement('p');
    num.className = 'l-calendar__heading-num';
    num.textContent = String(monthData.month);

    const label = document.createElement('p');
    label.className = 'l-calendar__heading-label';
    label.textContent = `${MONTH_LABELS[monthData.month - 1]} ${monthData.year}`;

    heading.append(num, label);
    return heading;
  }

  createMonth(monthData, index) {
    const month = document.createElement('section');
    month.className = 'l-calendar__month';
    month.dataset.monthIndex = String(index);
    if (this.months.length > 1) {
      month.id = `l-calendar-panel-${this.instanceId}-${index}`;
      month.setAttribute('role', 'tabpanel');
      month.setAttribute('aria-labelledby', `l-calendar-tab-${this.instanceId}-${index}`);
    }

    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    WEEK_DAYS.forEach((day) => {
      const th = document.createElement('th');
      th.className = 'l-calendar__head';
      th.textContent = day;
      headRow.appendChild(th);
    });
    thead.appendChild(headRow);

    const tbody = document.createElement('tbody');
    this.buildWeeks(monthData.year, monthData.month, monthData.closedDates).forEach((week) => {
      const row = document.createElement('tr');
      week.forEach((cellData) => {
        const cell = document.createElement('td');
        cell.className = 'l-calendar__day';
        const inner = document.createElement('div');
        inner.className = 'l-calendar__cell';

        if (!cellData) {
          inner.classList.add('is-empty');
        } else {
          const date = document.createElement('span');
          date.className = 'l-calendar__date';
          if (cellData.muted) date.classList.add('is-muted');
          date.textContent = String(cellData.day);
          inner.appendChild(date);

          if (cellData.status === 'open' || cellData.status === 'close') {
            const status = document.createElement('span');
            status.className = `l-calendar__status l-calendar__status--${cellData.status}`;
            status.setAttribute('aria-label', cellData.status === 'open' ? '営業日' : '定休日');
            inner.appendChild(status);
          }
        }

        cell.appendChild(inner);
        row.appendChild(cell);
      });
      tbody.appendChild(row);
    });

    const table = document.createElement('table');
    table.className = 'l-calendar__table';
    table.append(thead, tbody);
    month.append(this.createHeading(monthData), table);
    return month;
  }

  createTabNavigation() {
    const nav = document.createElement('div');
    nav.className = 'l-calendar__tabs';
    nav.setAttribute('role', 'tablist');
    nav.setAttribute('aria-label', '診療カレンダー');
    nav.addEventListener('click', this.handleTabClick);
    nav.addEventListener('keydown', this.handleTabKeydown);

    this.months.forEach((monthData, index) => {
      const button = document.createElement('button');
      button.className = 'l-calendar__tab';
      button.type = 'button';
      button.id = `l-calendar-tab-${this.instanceId}-${index}`;
      button.dataset.monthIndex = String(index);
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', `l-calendar-panel-${this.instanceId}-${index}`);
      button.appendChild(this.createHeading(monthData));
      nav.appendChild(button);
    });

    return nav;
  }

  handleTabClick(event) {
    const button = event.target.closest('.l-calendar__tab');
    if (!button || !this.tabNav.contains(button)) return;
    this.setActiveMonth(Number(button.dataset.monthIndex));
  }

  handleTabKeydown(event) {
    const currentIndex = this.tabButtons.findIndex((b) => b === event.target.closest('.l-calendar__tab'));
    if (currentIndex < 0) return;

    const keyMap = {
      ArrowRight: (currentIndex + 1) % this.tabButtons.length,
      ArrowLeft: (currentIndex - 1 + this.tabButtons.length) % this.tabButtons.length,
      Home: 0,
      End: this.tabButtons.length - 1,
    };

    if (!(event.key in keyMap)) return;
    event.preventDefault();
    const nextIndex = keyMap[event.key];
    this.setActiveMonth(nextIndex);
    this.tabButtons[nextIndex]?.focus();
  }

  setActiveMonth(index) {
    this.activeMonthIndex = index;
    this.syncView();
  }

  syncView() {
    const isMobile = this.mobileMediaQuery.matches;

    if (this.tabNav) {
      this.tabNav.hidden = !isMobile;
      this.tabButtons.forEach((button, index) => {
        const isActive = index === this.activeMonthIndex;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', String(isActive));
        button.setAttribute('tabindex', isActive ? '0' : '-1');
      });
    }

    this.monthElements.forEach((el, index) => {
      el.hidden = isMobile && index !== this.activeMonthIndex;
    });
  }

  handleBreakpointChange() {
    this.syncView();
  }

  destroy() {
    if (this.mobileMediaQuery) {
      if (typeof this.mobileMediaQuery.removeEventListener === 'function') {
        this.mobileMediaQuery.removeEventListener('change', this.handleBreakpointChange);
      } else if (typeof this.mobileMediaQuery.removeListener === 'function') {
        this.mobileMediaQuery.removeListener(this.handleBreakpointChange);
      }
    }
    if (this.tabNav) {
      this.tabNav.removeEventListener('click', this.handleTabClick);
      this.tabNav.removeEventListener('keydown', this.handleTabKeydown);
    }
  }

  render() {
    this.inner.textContent = '';
    this.monthElements = this.months.map((monthData, index) => this.createMonth(monthData, index));
    this.tabNav = this.months.length > 1 ? this.createTabNavigation() : null;
    this.tabButtons = this.tabNav ? Array.from(this.tabNav.querySelectorAll('.l-calendar__tab')) : [];

    if (this.tabNav) this.inner.appendChild(this.tabNav);
    this.monthElements.forEach((el) => this.inner.appendChild(el));
    this.syncView();
  }
}
